import { useEffect, useState } from "react";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import SkeletonText from "components/ui/skeleton/SkeletonText";
import toast from "components/ui/toast/Toast";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import Tooltip from "components/ui/tooltip/Tooltip";
import { copyToClipboard } from "lib/clipboard";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { ChannelRow, ChannelValueSpec } from "./contactChannels";

/**
 * Renders revealed contact channels as one line each — the presentation the contacts table
 * defines and every other surface matches. Callers supply the rows (see `visibleChannels`) and
 * the per-contact tracking attributes; what a line looks like, and what you can do with it,
 * lives here so the table and the lead's contact cards can't drift apart.
 *
 * Deliberately not responsible for the reveal itself: the table's resting glyph row, its eye,
 * and the "Unavailable"/"Not found" states stay with the table, which is the only surface that
 * reveals from a resting state.
 */

// In-row links read as quiet text rather than blue links: a dotted underline, and the colour
// left to the containing line so a link and its channel glyph darken together on hover. Exported
// because the table's buyer link is the same quiet treatment, not a channel of its own.
export const DOTTED_UNDERLINE = {
  textDecoration: "underline dotted",
  textDecorationSkipInk: "none",
  textUnderlinePosition: "from-font",
} as const;

// Per-line cues — the copy affordance and the external-link mark — surface only on the line you
// are pointing at, rather than every line at once. Faded rather than removed so the line never
// reflows as they appear, and so they stay focusable for keyboard use (see ChannelLine, which
// owns the reveal).
const LINE_CUE_CLASS = "contacts-line-cue";

const CopyButton = styled.button({
  display: "inline-flex",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
});

// Copy-to-clipboard affordance for a revealed value. Swaps to a check-in-circle for ~2s
// and shows a "…copied" confirmation inline. Radix dismisses the tooltip on click, so we
// drive its open state: hover as normal, but force it open while `copied`.
function CopyChip({
  value,
  label,
  kind,
  trackingData,
}: {
  value: string;
  label: string;
  kind: "phone" | "email";
  trackingData: EventData;
}) {
  const [copied, setCopied] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const { logEvent } = useTracking();
  const copiedLabel = kind === "email" ? "Email copied" : "Phone copied";

  // Revert the confirmation after 2s; the cleanup covers both unmount
  // (row paged/filtered away) and the next toggle.
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const handleCopy = async () => {
    const ok = await copyToClipboard({ plain: value });
    if (!ok) {
      toast.error("Couldn't copy to clipboard.");
      return;
    }
    logEvent(
      kind === "email" ? EventNames.emailCopied : EventNames.phoneNumberCopied,
      trackingData,
    );
    setCopied(true);
  };

  return (
    <Tooltip
      content={copied ? copiedLabel : label}
      open={copied || hoverOpen}
      onOpenChange={setHoverOpen}
    >
      <CopyButton
        type="button"
        aria-label={copied ? copiedLabel : label}
        onClick={handleCopy}
        className={LINE_CUE_CLASS}
      >
        <UIcon
          icon={copied ? "checkCircle" : "copy03"}
          size={16}
          color={copied ? "sysSuccessDefault" : "sysTextSecondary"}
        />
      </CopyButton>
    </Tooltip>
  );
}

/**
 * How prominent a value reads. `secondary` inherits the line, level with every other column of a
 * table row; `default` lifts it to the colour and weight of the lead card's detail values, so a
 * phone number sits at the same level as the seniority and buyer opposite it. Glyphs, the copy
 * affordance and the external-link cue keep the line's colour either way.
 */
export type ChannelValueTone = "secondary" | "default";

/** The tone as plain CSS. `secondary` adds nothing, so it renders exactly as it always has. */
function toneStyles(theme: Theme, tone: ChannelValueTone) {
  if (tone !== "default") return {};
  return { color: theme.colors.sysTextDefault, fontWeight: 500 };
}

const PhoneValue = styled.span<{ tone: ChannelValueTone }>(({ theme, tone }) =>
  toneStyles(theme, tone),
);

// The first number reads in full; the rest are a count with the numbers behind a tooltip, so a
// contact with several lines still occupies one row.
function PhoneNumbersValue({ numbers, tone }: { numbers: string[]; tone: ChannelValueTone }) {
  const [first, ...rest] = numbers;
  return (
    <>
      <PhoneValue tone={tone}>{first}</PhoneValue>
      {rest.length > 0 && (
        <Tooltip content={rest.join(", ")}>
          <Text inline color="sysTextSecondary">
            (+{rest.length})
          </Text>
        </Tooltip>
      )}
    </>
  );
}

// Revealed email. Truncates to the right so a long address clips inside its container
// instead of running under the copy affordance. EllipsisTooltip shows the full value,
// but only when it is actually truncated.
const EmailLink = styled.a<{ tone: ChannelValueTone }>(({ theme, tone }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  "&&, &&:hover": { ...DOTTED_UNDERLINE, color: "inherit", ...toneStyles(theme, tone) },
}));

function EmailChannelValue({ email, tone }: { email: string; tone: ChannelValueTone }) {
  return (
    <EllipsisTooltip fullText={email}>
      {(ref) => (
        <EmailLink ref={ref} tone={tone} href={`mailto:${email}`}>
          {email}
        </EmailLink>
      )}
    </EllipsisTooltip>
  );
}

// antd's reset styles bare `a` and `a:hover` with the link blue. A single class outranks the
// former but not the latter, so these are doubled (`&&`) to hold the inherited colour through
// hover — otherwise the text jumps to link-blue while its glyph darkens.
const LinkedinLink = styled.a<{ tone: ChannelValueTone }>(({ theme, tone }) => ({
  minWidth: 0,
  "&&, &&:hover": { ...DOTTED_UNDERLINE, color: "inherit", ...toneStyles(theme, tone) },
}));

const ExternalCue = styled.span({ display: "inline-flex", flexShrink: 0 });

function LinkedinChannelValue({
  url,
  trackingData,
  tone,
}: {
  url: string;
  trackingData: EventData;
  tone: ChannelValueTone;
}) {
  const { logEvent } = useTracking();
  return (
    <>
      <LinkedinLink
        tone={tone}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          // The line may sit inside a clickable container (a table row, a card header).
          e.stopPropagation();
          logEvent(EventNames.linkedinClicked, trackingData);
        }}
      >
        LinkedIn
      </LinkedinLink>
      <ExternalCue className={LINE_CUE_CLASS} aria-hidden>
        <UIcon icon="linkExternal02" size={16} />
      </ExternalCue>
    </>
  );
}

function ChannelSpecValue({
  spec,
  trackingData,
  tone,
}: {
  spec: ChannelValueSpec;
  trackingData: EventData;
  tone: ChannelValueTone;
}) {
  switch (spec.kind) {
    case "phones":
      return <PhoneNumbersValue numbers={spec.numbers} tone={tone} />;
    case "email":
      return <EmailChannelValue email={spec.email} tone={tone} />;
    case "linkedin":
      return <LinkedinChannelValue url={spec.url} trackingData={trackingData} tone={tone} />;
  }
}

/** Channel glyph — never shrinks, so a long value truncates instead of squashing the icon. */
export const ChannelIcon = styled.span({ display: "inline-flex", flexShrink: 0 });

// Every revealed line is secondary text, glyph included — the glyph inherits it rather than
// fixing its own colour. Each line also owns its cues: hidden until you point at that line (or
// tab into it), and always shown on coarse pointers where hover never fires.
const cueVisible = { opacity: 1 };
const ChannelLine = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  [`.${LINE_CUE_CLASS}`]: { opacity: 0, transition: "opacity 0.1s ease" },
  [`&:hover .${LINE_CUE_CLASS}`]: cueVisible,
  [`&:focus-within .${LINE_CUE_CLASS}`]: cueVisible,
  "@media (hover: none)": { [`.${LINE_CUE_CLASS}`]: cueVisible },
}));

// A link line (email, LinkedIn) is one hover target: glyph and link text darken together.
const LinkChannelLine = styled(ChannelLine)(({ theme }) => ({
  "&:hover": { color: theme.colors.sysTextDefault },
}));

/**
 * One line per channel: glyph, value, and the value's own affordances (copy, or the
 * external-link mark). `revealing` swaps every value for a skeleton of its own width, so the
 * list keeps its shape while a reveal is in flight.
 */
export function ContactChannelList({
  rows,
  trackingData,
  revealing = false,
  valueTone = "secondary",
}: {
  rows: ChannelRow[];
  /** Per-contact Amplitude attributes, applied to every event a line fires. */
  trackingData: EventData;
  revealing?: boolean;
  /** See {@link ChannelValueTone}. Defaults to the table's treatment. */
  valueTone?: ChannelValueTone;
}) {
  return (
    <Flex column gap={8} minWidth={0} width="100%">
      {rows.map((row) => {
        // A link line darkens on hover; both kinds inherit their glyph colour from the line.
        const Line = row.isLink ? LinkChannelLine : ChannelLine;
        return (
          <Line key={row.key} alignItems="center" gap={8} minWidth={0}>
            <ChannelIcon
              role={row.ariaLabel && !row.isLink ? "img" : undefined}
              aria-label={row.isLink ? undefined : row.ariaLabel}
            >
              <UIcon icon={row.icon} size={16} />
            </ChannelIcon>
            {revealing ? (
              <SkeletonText as="span" width={row.skeletonWidth} customSpacing />
            ) : (
              <>
                {row.value && (
                  <ChannelSpecValue spec={row.value} trackingData={trackingData} tone={valueTone} />
                )}
                {row.copy && (
                  <CopyChip
                    value={row.copy.value}
                    label={row.copy.label}
                    kind={row.copy.kind}
                    trackingData={trackingData}
                  />
                )}
              </>
            )}
          </Line>
        );
      })}
    </Flex>
  );
}
